import { Box, Checkbox, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { ThumbnailImage } from '../ThumbnailImage';
import { ReusePlaceholder, DeletedPlaceholder } from '../placeholders';
import { CatalogViewProps } from './types';

const RETIRED_STATUSES = new Set(['CLEARED', 'DELETED']);

/**
 * Image-only view — tight grid of thumbnails with no text metadata.
 * Tooltip shows title on hover.
 */
export const CatalogImageView = ({
  items,
  browseItemIds,
  browseOwnerIds,
  routePrefix,
  browseTotal,
  browsePageOffset,
  browsePage,
  browseQuery,
  browsePageSize,
  isSelected,
  onToggleSelect,
  selectionMode,
  buildItemLink,
}: CatalogViewProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isLaptop = useMediaQuery(theme.breakpoints.down('lg'));

  const columns = isMobile ? 3 : isTablet ? 5 : isLaptop ? 6 : 8;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: 0.5,
        p: 0.5,
      }}
    >
      {items.map((item, index) => {
        const title = item.status === 'CLEARED' ? 'Available for Reuse' : (item.title || 'Untitled');
        const thumbnailUrl = item.coverImageUrl || null;
        const isProcessing =
          item.processingStatus === 'UPLOADING' ||
          item.processingStatus === 'PROCESSING';
        const isFailed = item.processingStatus === 'FAILED';
        const isRetired = RETIRED_STATUSES.has(item.status ?? '');
        // DESTROYED is tracked separately from RETIRED_STATUSES: the real
        // artwork image must still render (destruction is a historical
        // record), but we still want the tile dimmed as a visual cue.
        const isDestroyed = item.status === 'DESTROYED';

        const itemLink = buildItemLink
          ? buildItemLink(item)
          : { to: `/object-details/${item.catalogItemId}`, routePrefix: '/object-details' };

        return (
          <Box
            key={`image-${item.catalogItemId}`}
            component={Link}
            to={itemLink.to}
            state={browseItemIds ? { itemIds: browseItemIds, currentIndex: index, routePrefix: itemLink.routePrefix || routePrefix || '/object-details', total: browseTotal, pageOffset: browsePageOffset, browsePage, query: browseQuery, pageSize: browsePageSize, itemOwnerIds: browseOwnerIds } : undefined}
            title={title}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              aspectRatio: '1',
              overflow: 'hidden',
              borderRadius: 0.5,
              position: 'relative',
              bgcolor: isProcessing || isFailed ? 'grey.100' : 'transparent',
              ...((isRetired || isDestroyed) && { opacity: 0.6 }),
              ...(selectionMode && isSelected?.(item.catalogItemId) && {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: -2,
              }),
              '&:hover': {
                opacity: 0.85,
              },
              ...(selectionMode && {
                '&:hover .selection-checkbox': {
                  opacity: 1,
                },
              }),
            }}
          >
            {selectionMode && onToggleSelect && (
              <Checkbox
                size="small"
                className="selection-checkbox"
                checked={isSelected?.(item.catalogItemId) ?? false}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggleSelect(item.catalogItemId);
                }}
                sx={{
                  position: 'absolute',
                  top: 2,
                  left: 2,
                  zIndex: 1,
                  bgcolor: 'rgba(255, 255, 255, 0.85)',
                  borderRadius: '4px',
                  p: 0.25,
                  opacity: isSelected?.(item.catalogItemId) ? 1 : 0,
                  transition: 'opacity 0.15s',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
                }}
              />
            )}
            {isProcessing ? (
              <CircularProgress data-testid="processing-indicator" size={20} color="primary" />
            ) : isFailed ? (
              <ErrorOutline data-testid="processing-failed-indicator" sx={{ fontSize: 20, color: 'error.main' }} />
            ) : isRetired ? (
              item.status === 'CLEARED' ? (
                <ReusePlaceholder variant="thumbnail" />
              ) : (
                <DeletedPlaceholder variant="thumbnail" />
              )
            ) : (
              <ThumbnailImage src={thumbnailUrl} alt={title} />
            )}
          </Box>
        );
      })}
    </Box>
  );
};
